<template>
  <div>
    <h1>
      FREQUENTLY ASKED QUESTIONS
    </h1>
    <v-container fluid class="l-container--width-80">
      <section v-for="(section, index) in faqSections" :key="index">
        <v-row>
          <v-col class="header-col">
            <h2>{{ section.sectionName }}</h2>
          </v-col>
        </v-row>
        <v-row justify="center" class="pt-6">
          <v-expansion-panels focusable>
            <v-expansion-panel
              bg-color="background"
              v-for="(item, i) in section.faqItems"
              :key="i"
              class="mb-2"
            >
              <v-expansion-panel-title
                ><h3>{{ item.question }}</h3></v-expansion-panel-title
              >
              <v-expansion-panel-text class="text-left">
                <div v-if="item.textAnswer" class="pt-4">
                  {{ item.textAnswer }}
                </div>
                <div
                  v-if="item.htmlAnswer"
                  v-html="item.htmlAnswer"
                  class="pt-4"
                ></div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-row>
      </section>
    </v-container>
  </div>
</template>

<script>
export default {
  name: "Faq",
  data() {
    return {
      faqSections: {
        generalFaqs: {
          sectionName: "General",
          faqItems: [
            {
              question: "How is ATT&CK used for evaluations?",
              textAnswer:
                'The evaluations use adversary emulation, which is a way of testing "in the style of" a specific adversary. This allows us to select a relevant subset of ATT&CK techniques to test. To generate our emulation plans, we use public threat intel reporting, map it to ATT&CK, and then determine a way to replicate the behaviors.',
              htmlAnswer: null,
            },
            {
              question: "How do these evaluations relate to ATT&CK?",
              textAnswer:
                "ATT&CK evaluations are built on the publicly-available information captured by ATT&CK, but they are separate from the ongoing work to maintain the ATT&CK knowledge base. The team who maintains ATT&CK will continue to accept contributions from anyone in the community. The ATT&CK knowledge base will remain free and open to everyone, and vendor participation in the evaluations has no influence on that process.",
              htmlAnswer: null,
            },
            {
              question: "What can I do to help?",
              textAnswer: null,
              htmlAnswer: `<ul>
                            <li>Feedback. We are always looking for feedback on what works and what doesn’t in our results and methodology. Learning how you use the results and what you want to get out of them helps us shape our work to help you, and your peers. <a class="c-text--hover" href="mailto:evals@mitre-engenuity.org">Provide feedback</a>.</li>
                            <li>Intel. We frame our evaluations in the context of the known threat to ensure our results are relevant and useful. These emulations are driven by available intel. <a class="c-text--hover" href="https://medium.com/mitre-attack/open-invitation-to-share-cyber-threat-intelligence-on-apt29-for-adversary-emulation-plan-831c8c929f31">If you share your insights</a>, you can improve our plans. We welcome ideas for adversaries to emulate, as well as intel to help inform potential usage in our evaluation rounds</li>
                       </ul>`,
            },
          ],
        },
        participationFaqs: {
          sectionName: "Participation",
          faqItems: [
            {
              question:
                "Where can I find information on when rounds were executed, who participated, etc.?",
              textAnswer: null,
              htmlAnswer: `Each evaluation type (e.g., Enterprise or ICS) and round (e.g., APT3 or TRITON) has available information specific to those evaluations on this site. An overview page is available for each round to provide basic high level information, including the participants. For example, APT29's overview page is available  <a class="c-text--hover" href="/enterprise/apt29" target="_blank">here</a>.`,
            },
            {
              question: "How does participation relate across rounds?",
              textAnswer: "Each round's participation is independent.",
              htmlAnswer: null,
            },
            {
              question: "Who can participate?",
              textAnswer: null,
              htmlAnswer: `Participation is focused on capability type (e.g., detection of ATT&CK behaviors) rather than market segment (e.g., EDR, EPP, etc.). If a technology addresses the capability, there may be additional technical requirements. For example, Enterprise evaluations have the following requirements:
            <br />
            <br />
            <ul>
                <li>Technology must address post-compromise behaviors as described by ATT&CK</li>
                <li>Technology must deploy into the Microsoft Azure environment</a></li>
                <li>Sensor/data, beyond those provided by default in Azure, must be provided by the vendor</li>
                <li>For detection evaluations, capabilities that could prevent the successful execution of our emulation (e.g., protections, preventions, responses) must be disabled. The sensors that drive these actions can still be used as data sources to identify behavior</li>
            </ul>
            <br />
            <em>Vendor participation is subject to applicable legal restrictions, available resources, and other factors.</em>`,
            },
            {
              question: "How can I participate?",
              textAnswer: null,
              htmlAnswer: `Visit the <a class="c-text--hover" href="https://mitre-engenuity.org/cybersecurity/attack-evaluations/#get-evaluated">Get Evaluated</a> section for more details on how to participate.`,
            },
            {
              question: "What do participants get out of this?",
              textAnswer:
                "Vendors get a third-party evaluation of their capabilities ability to address adversary behaviors, as described by ATT&CK. These evaluations are not ATT&CK certifications, nor are they a guarantee that you are protected against the adversary we are emulating. Adversary behavior changes over time. The evaluations provide vendors with insight and confidence into how their capabilities map to ATT&CK techniques. Equally important, because we are publicly releasing the results, we enable their customers, and potential customers, to understand how to utilize their tools to defend ATT&CK-categorized behaviors.",
              htmlAnswer: null,
            },
            {
              question: "Are participants paying to perform evaluations?",
              textAnswer:
                "Yes. There was significant demand for unbiased ATT&CK evaluations and we needed to create a mechanism to open up evaluations to the security vendor market.",
              htmlAnswer: null,
            },
            {
              question: "What information was available to participants?",
              textAnswer:
                "All vendors receive the same information prior to evaluations. The majority of this information also gets posted on this site to ensure transparency. Vendors receive the scope of the evaluation (e.g., techniques that could be used), a named threat for whom the behaviors are being replicated, environment information, and a general idea of what data will be collected. Details on how the techniques will be implemented are not provided.",
              htmlAnswer: null,
            },
            {
              question: "What can I do if I can’t participate?",
              textAnswer:
                "Let us know your needs, and the current limitations of our methodology. This will help us shape our evaluation road map.",
              htmlAnswer: null,
            },
            {
              question: "Can a vendor opt out of releasing their results?",
              textAnswer:
                "No, all vendors signing up for the evaluation agree to have their results publicly released upon conclusion of their test.",
              htmlAnswer: null,
            },
            {
              question:
                "In the APT3 evaluation, what is the difference between Initial Cohort and Rolling Admissions?",
              textAnswer:
                "Rolling admissions participants had access to additional information during the evaluation process due to the launch of the website, which contained the initial cohort’s results and the methodology. Cybereason's and FireEye's feedback period occurred after the launch of the ATT&CK Evaluations website. F-Secure, McAfee, and Palo Alto Networks entire evaluation process took place after the ATT&CK Evaluations website was launched.",
              htmlAnswer: null,
            },
          ],
        },
        processFaqs: {
          sectionName: "Process",
          faqItems: [
            {
              question: "How does the evaluation process work?",
              textAnswer: "",
              htmlAnswer: `The ATT&CK evaluations are based on a four-phased approach:
                        <br />
                        <br />
                        <ol>
                            <li><strong>Setup</strong>: The vendor installs their tool in a MITRE provided cyber range. </li>
                            <li><strong>Evaluation</strong>: During a joint evaluation session, MITRE adversary emulators ("red team") execute an emulation in the style of an adversary group, technique-by-technique. The vendor being tested will provide the personnel who review tool output ("blue team"). MITRE provides the personnel to oversee the evaluation and facilitate communication between red and blue, as well as capture results ("white team").</li>
                            <li><strong>Feedback</strong>: Vendors are provided an opportunity to offer feedback on the preliminary results, but the feedback does not obligate MITRE to make any modification to the results.</li>
                            <li><strong>Release</strong>: MITRE publicly releases the evaluation methodology and results of the tool evaluations. </li>
                        </ol>
                        <br />
                        For additional details refer to our <a class="c-text--hover" href="/methodology-overview">methodology.</a>`,
            },
            {
              question: "Do you use the ATT&CK matrix stoplight chart?",
              textAnswer:
                "The stoplight chart (which uses red, yellow, and green to indicate level of confidence for detection of techniques) has been used since ATT&CK's creation because it is a simple yet powerful way to understand ATT&CK coverage. While a stoplight chart may be useful to show coverage and gaps, we do not use this visualization because it is not granular enough to convey our results.",
              htmlAnswer: null,
            },
            {
              question: "Does this mean that all detections are created equal?",
              textAnswer: null,
              htmlAnswer: `There is no one way to look at detection data. Some key concepts to consider include:
                        <br />
                        <br />
                        <ol>
                            <li>Availability — Is the detection capability gathering the necessary data?</li>
                            <li>Efficacy — Can the gathered data be processed into meaningful information?</li>
                            <li>Actionability — Is the provided information sufficient to act on?</li>
                        </ol>`,
            },
          ],
        },
      },
    };
  },
};
</script>

<style scoped lang="scss">
  @import "../sass/pages/_faq.scss";
</style>
